import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { cn } from './cn.js';

/// Folga entre o campo e o painel, e do painel para a borda da janela.
const GAP = 8;
const MARGIN = 8;
/// Abaixo disto o painel deixa de ser útil: é preferível espremer o outro lado.
const MIN_HEIGHT = 176;

export interface FloatingPanelProps {
  /// O campo ao qual o painel se ancora; clique dentro dele não fecha.
  anchorRef: RefObject<HTMLElement | null>;
  onDismiss: () => void;
  /// `anchor` copia a largura do campo, `min-anchor` usa a do campo como piso, `auto` deixa o
  /// painel com a largura das próprias classes.
  width?: 'anchor' | 'min-anchor' | 'auto' | undefined;
  /// `end` alinha a borda direita do painel à do campo, para âncoras no canto direito do formulário.
  align?: 'start' | 'center' | 'end' | undefined;
  id?: string | undefined;
  role?: string | undefined;
  'aria-label'?: string | undefined;
  className?: string | undefined;
  children: ReactNode;
}

interface Position {
  left: number;
  top?: number;
  bottom?: number;
  width?: number;
  minWidth?: number;
  maxHeight?: number;
}

/// Camada flutuante de um campo — calendário, lista de opções, seletor de mês.
///
/// Um painel posicionado dentro do próprio campo pertence ao que rola em volta: no modal ele
/// esticava o corpo, criava rolagem e ficava cortado pela borda do diálogo; virado para cima,
/// sumia atrás do cabeçalho. Aqui ele é levado para uma camada própria do diálogo — ou do documento
/// quando não há diálogo — e posicionado pelo retângulo real do campo, então cobre o que estiver
/// embaixo em vez de disputar espaço. Fica dentro do diálogo de propósito: portal solto no `body`
/// seria "fora" para o Radix, que fecharia o modal ao primeiro clique no painel.
export function FloatingPanel({
  anchorRef,
  onDismiss,
  width = 'auto',
  align = 'start',
  id,
  role,
  'aria-label': ariaLabel,
  className,
  children,
}: FloatingPanelProps) {
  const [host, setHost] = useState<Element | null>(null);
  const [position, setPosition] = useState<Position | null>(null);
  const layer = useRef<HTMLDivElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  /// O limite e a largura que o painel pediria sem interferência, medidos antes de qualquer limite
  /// nosso: medir de novo depois de aplicar o limite devolveria o próprio limite e o painel
  /// oscilaria a cada rolagem. A altura do conteúdo vem do `scrollHeight`, que o limite não muda, e
  /// é lida a cada medida: a lista que chega do servidor cresce depois de o painel abrir.
  const natural = useRef<{ cap: number; width: number } | null>(null);
  const content = useRef<MutationObserver | null>(null);

  const measure = useCallback(() => {
    const anchor = anchorRef.current?.getBoundingClientRect();
    const frame = layer.current?.getBoundingClientRect();
    const node = panel.current;
    if (!anchor || !frame || !node) return;
    natural.current ??= { cap: maxHeightOf(node), width: node.offsetWidth };
    const desired = Math.min(node.scrollHeight, natural.current.cap);
    const below = window.innerHeight - anchor.bottom - GAP - MARGIN;
    const above = anchor.top - GAP - MARGIN;
    const downward = desired <= below || below >= above;
    const room = Math.max(downward ? below : above, MIN_HEIGHT);
    const span =
      width === 'anchor'
        ? anchor.width
        : width === 'min-anchor'
          ? Math.max(natural.current.width, anchor.width)
          : natural.current.width;
    const start =
      align === 'center'
        ? anchor.left + anchor.width / 2 - span / 2
        : align === 'end'
          ? anchor.right - span
          : anchor.left;
    // A camada pode não começar no canto da janela: dentro do modal ela nasce na área do overlay.
    // Medir o retângulo dela é o que mantém a conta correta nos dois casos.
    setPosition({
      left: Math.max(MARGIN, Math.min(start, window.innerWidth - span - MARGIN)) - frame.left,
      ...(downward
        ? { top: anchor.bottom + GAP - frame.top }
        : { bottom: frame.bottom - anchor.top + GAP }),
      ...(width === 'anchor' ? { width: anchor.width } : {}),
      ...(width === 'min-anchor' ? { minWidth: anchor.width } : {}),
      ...(desired > room ? { maxHeight: room } : {}),
    });
  }, [align, anchorRef, width]);

  // A medição acontece quando a camada entra no documento, não num efeito: a posição precisa estar
  // resolvida antes da primeira pintura, senão o painel pisca no canto da tela.
  const attachLayer = useCallback(
    (node: HTMLDivElement | null) => {
      layer.current = node;
      content.current?.disconnect();
      content.current = null;
      if (!node) return;
      measure();
      // O conteúdo muda com o painel aberto — "buscando…" vira a lista de resultados —, e o lado
      // escolhido para a altura antiga deixaria a lista nova para fora da janela.
      if (panel.current) {
        content.current = new MutationObserver(measure);
        content.current.observe(panel.current, { childList: true, subtree: true });
      }
    },
    [measure],
  );

  // Onde a camada mora: dentro do diálogo quando há um, senão no documento.
  const attachHost = useCallback((node: HTMLSpanElement | null) => {
    if (node) setHost(node.closest('[role="dialog"]') ?? document.body);
  }, []);

  useEffect(() => {
    const dismiss = (event: PointerEvent) => {
      const target = event.target as Node;
      if (panel.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onDismiss();
    };
    document.addEventListener('pointerdown', dismiss);
    // A âncora se move quando a página ou o corpo do modal rola; `true` escuta os dois.
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [anchorRef, measure, onDismiss]);

  return (
    <>
      <span aria-hidden="true" className="hidden" ref={attachHost} />
      {host
        ? createPortal(
            <div ref={attachLayer} className="pointer-events-none fixed inset-0 z-50">
              <div
                id={id}
                role={role}
                aria-label={ariaLabel}
                ref={panel}
                style={{
                  position: 'absolute',
                  visibility: position ? 'visible' : 'hidden',
                  left: `${position?.left ?? 0}px`,
                  ...size(position),
                }}
                className={cn('pointer-events-auto overflow-y-auto overscroll-contain', className)}
              >
                {children}
              </div>
            </div>,
            host,
          )
        : null}
    </>
  );
}

function size(position: Position | null) {
  if (!position) return {};
  return {
    ...(position.top === undefined ? {} : { top: `${position.top}px` }),
    ...(position.bottom === undefined ? {} : { bottom: `${position.bottom}px` }),
    ...(position.width === undefined ? {} : { width: `${position.width}px` }),
    ...(position.minWidth === undefined ? {} : { minWidth: `${position.minWidth}px` }),
    ...(position.maxHeight === undefined ? {} : { maxHeight: `${position.maxHeight}px` }),
  };
}

/// O teto que as próprias classes do painel definem, quando existe.
function maxHeightOf(node: HTMLElement): number {
  const declared = Number.parseFloat(window.getComputedStyle(node).maxHeight);
  return Number.isFinite(declared) ? declared : Number.POSITIVE_INFINITY;
}
